// Persistência das abas de conformidade do condomínio (P-14, P-15, P-20).
//
// As abas Segurança e Licenças eram HTML puro: `<select>` sem `value` e um
// botão "Salvar" sem `onClick`. A de Alvarás calculava os templates aplicáveis
// e não tinha como registrar nenhum deles.
//
// **Documento não é duplicado.** AVCB (`tmpl-001`) e Habite-se (`tmpl-005`) são
// templates de alvará e vivem em `alvaras_condominio`, com número, emissão,
// vencimento e anexo. As abas leem de lá em vez de guardar um segundo sim/não —
// senão a aba Alvarás diria "vencido em 2024" enquanto a Segurança dizia "Sim,
// possui", e as duas telas estariam igualmente convictas.

import { supabase } from '@/lib/supabase';

/** Templates que as abas Segurança e Licenças exibem sem duplicar. */
export const TEMPLATE_AVCB = 'tmpl-001';
export const TEMPLATE_HABITE_SE = 'tmpl-005';
export const TEMPLATE_REFORMA = 'tmpl-012';

export type SituacaoAlvara = 'nao_se_aplica' | 'pendente' | 'a_vencer' | 'vencido' | 'vigente';

export interface AlvaraRegistro {
  id: string;
  condominioId: string;
  templateId: string | null;
  nome: string;
  descricao: string;
  categoria: string;
  status: 'pendente' | 'vigente' | 'vencido' | 'nao_se_aplica';
  situacao: SituacaoAlvara;
  periodicidade: string;
  obrigatorio: boolean;
  orgaoEmissor: string;
  dataEmissao: string | null;
  dataVencimento: string | null;
  numeroDocumento: string | null;
  motivoNaoAplica: string | null;
  anexos: AnexoDeAlvara[];
}

/** Colunas lidas em toda consulta de alvará — uma lista só, para não divergirem. */
const COLUNAS_ALVARA =
  'id, condominio_id, template_id, nome, descricao, categoria, status, periodicidade, ' +
  'obrigatorio, orgao_emissor, data_emissao, data_vencimento, numero_documento, ' +
  'motivo_nao_aplica, anexos';

/**
 * Situação do alvará hoje.
 *
 * Espelha `situacao_do_alvara()` (migration 0029). A regra vive no banco, que é
 * quem o painel consulta; aqui ela é repetida porque a listagem já traz as
 * linhas e uma ida ao servidor por linha seria pior. As duas precisam concordar
 * — é o que o teste de banco e o unitário guardam.
 */
export function situacaoDoAlvara(
  status: string | null,
  dataVencimento: string | null,
  janelaDias: number,
): SituacaoAlvara {
  if (status === 'nao_se_aplica') return 'nao_se_aplica';
  if (!status || status === 'pendente') return 'pendente';
  if (!dataVencimento) return 'pendente';

  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);
  const [ano, mes, dia] = dataVencimento.slice(0, 10).split('-').map(Number);
  const venc = new Date(ano, mes - 1, dia);

  const dias = Math.round((venc.getTime() - hoje.getTime()) / 86400000);
  if (dias < 0) return 'vencido';
  if (dias <= janelaDias) return 'a_vencer';
  return 'vigente';
}

function paraRegistro(row: any, janelaDias: number): AlvaraRegistro {
  return {
    id: row.id,
    condominioId: row.condominio_id,
    templateId: row.template_id,
    nome: row.nome,
    descricao: row.descricao || '',
    categoria: row.categoria || '',
    status: row.status,
    situacao: situacaoDoAlvara(row.status, row.data_vencimento, janelaDias),
    periodicidade: row.periodicidade || 'anual',
    obrigatorio: !!row.obrigatorio,
    orgaoEmissor: row.orgao_emissor || '',
    dataEmissao: row.data_emissao,
    dataVencimento: row.data_vencimento,
    numeroDocumento: row.numero_documento,
    motivoNaoAplica: row.motivo_nao_aplica,
    anexos: Array.isArray(row.anexos) ? row.anexos : [],
  };
}

export async function lerAlvaras(
  condominioId: string,
  janelaDias: number,
): Promise<AlvaraRegistro[]> {
  const { data, error } = await supabase
    .from('alvaras_condominio')
    .select(COLUNAS_ALVARA)
    .eq('condominio_id', condominioId)
    .order('nome');

  if (error) throw new Error(error.message);
  return (data ?? []).map((row) => paraRegistro(row, janelaDias));
}

export interface AlvaraParaGravar {
  id?: string;
  condominioId: string;
  templateId: string | null;
  nome: string;
  descricao?: string;
  categoria?: string;
  status: 'pendente' | 'vigente' | 'vencido' | 'nao_se_aplica';
  periodicidade?: string;
  obrigatorio?: boolean;
  orgaoEmissor?: string;
  dataEmissao: string | null;
  dataVencimento: string | null;
  numeroDocumento: string | null;
  motivoNaoAplica: string | null;
  anexos: AnexoDeAlvara[];
}

/**
 * Grava o alvará.
 *
 * `id` ausente é registro novo; o índice único por `(condominio_id,
 * template_id)` impede que o mesmo template vire duas linhas quando alguém
 * abre a tela em duas abas. Licença avulsa tem `templateId` nulo e fica fora
 * do índice — o condomínio pode ter várias.
 */
export async function gravarAlvara(alvara: AlvaraParaGravar): Promise<string> {
  const linha = {
    condominio_id: alvara.condominioId,
    template_id: alvara.templateId,
    nome: alvara.nome,
    descricao: alvara.descricao ?? '',
    categoria: alvara.categoria ?? '',
    status: alvara.status,
    periodicidade: alvara.periodicidade ?? 'anual',
    obrigatorio: alvara.obrigatorio ?? false,
    orgao_emissor: alvara.orgaoEmissor ?? '',
    data_emissao: alvara.dataEmissao,
    data_vencimento: alvara.dataVencimento,
    numero_documento: alvara.numeroDocumento,
    motivo_nao_aplica: alvara.motivoNaoAplica,
    anexos: alvara.anexos,
    updated_at: new Date().toISOString(),
  };

  if (alvara.id) {
    const { error } = await supabase.from('alvaras_condominio').update(linha).eq('id', alvara.id);
    if (error) throw new Error(error.message);
    return alvara.id;
  }

  const { data, error } = await supabase
    .from('alvaras_condominio')
    .insert(linha)
    .select('id')
    .single();

  if (error) throw new Error(error.message);
  return data.id as string;
}

export async function excluirAlvara(id: string): Promise<void> {
  // DELETE vira `deletado_em` pelo gatilho da migration 0019.
  const { error } = await supabase.from('alvaras_condominio').delete().eq('id', id);
  if (error) throw new Error(error.message);
}

// ─── A que o registro se aplica (secao 5.2, migration 0049) ─────────
//
// O catálogo diz que o Alvará de Elevadores vale para elevadores. **Qual**
// elevador deste prédio o registro cobre é decisão do cliente: um condomínio
// pode ter um laudo por torre e outro um só para os quatro.
//
// O banco recusa vincular item de outro condomínio (gatilho
// `alvara_item_mesmo_condominio`), então um id vindo torto do formulário não
// atravessa a fronteira de Tenant.

export async function lerItensDoAlvara(alvaraId: string): Promise<string[]> {
  const { data, error } = await supabase
    .from('alvara_condominio_itens')
    .select('infraestrutura_id')
    .eq('alvara_id', alvaraId);

  if (error) throw new Error(error.message);
  return (data ?? []).map((linha) => linha.infraestrutura_id as string);
}

/**
 * Substitui o conjunto de itens cobertos pelo registro.
 *
 * Apaga e regrava, como os demais vínculos do módulo: o conjunto é pequeno e
 * precisa terminar exatamente igual ao que a tela mostra.
 */
export async function gravarItensDoAlvara(
  alvaraId: string,
  infraestruturaIds: string[],
): Promise<void> {
  const { error: erroRemocao } = await supabase
    .from('alvara_condominio_itens')
    .delete()
    .eq('alvara_id', alvaraId);
  if (erroRemocao) throw new Error(erroRemocao.message);

  if (infraestruturaIds.length === 0) return;

  const { error } = await supabase.from('alvara_condominio_itens').upsert(
    infraestruturaIds.map((infraestrutura_id) => ({ alvara_id: alvaraId, infraestrutura_id })),
    { onConflict: 'alvara_id,infraestrutura_id' },
  );
  if (error) throw new Error(error.message);
}

// ─── Documento digitalizado ─────────────────────────────────────────
// Buckets privados: a leitura passa por URL assinada, e o caminho começa pelo id
// do condomínio, que é o que a policy usa para decidir de quem é o arquivo
// (`documento_do_meu_condominio`, migration 0032).
//
// Alvará e manutenção compartilham a forma do anexo e as três operações; o que
// muda é o bucket. Duplicar upload, URL assinada e remoção por bucket seria
// manter duas cópias da mesma regra de caminho.

export type BucketDeDocumento = 'alvaras' | 'manutencoes' | 'documentos';

/**
 * Anexo guardado no jsonb da linha dona.
 *
 * `caminho` é a chave no bucket, nunca URL: URL assinada expira, e guardar uma
 * expirada no banco seria guardar um link quebrado.
 */
export interface AnexoDeDocumento {
  caminho: string;
  nome: string;
  tamanho: number;
  enviadoEm: string;
}

/** Mantido pelo nome antigo porque a aba Alvarás já o usa. */
export type AnexoDeAlvara = AnexoDeDocumento;

export async function enviarDocumento(
  bucket: BucketDeDocumento,
  condominioId: string,
  donoId: string,
  arquivo: File,
): Promise<AnexoDeDocumento> {
  const extensao = arquivo.name.split('.').pop() || 'pdf';
  // O nome original não entra no caminho: acento, espaço e barra viram chave
  // inválida no storage. Ele viaja no jsonb, que é de onde a tela o mostra.
  const caminho = `${condominioId}/${donoId}/${Date.now()}.${extensao}`;

  const { error } = await supabase.storage
    .from(bucket)
    .upload(caminho, arquivo, { contentType: arquivo.type, upsert: false });

  if (error) throw new Error(error.message);

  return {
    caminho,
    nome: arquivo.name,
    tamanho: arquivo.size,
    enviadoEm: new Date().toISOString(),
  };
}

/** URL assinada, válida por uma hora. Bucket privado não serve URL pública. */
export async function urlDoDocumento(
  bucket: BucketDeDocumento,
  caminho: string,
): Promise<string | null> {
  const { data, error } = await supabase.storage.from(bucket).createSignedUrl(caminho, 3600);
  if (error) return null;
  return data?.signedUrl ?? null;
}

export async function removerDocumento(bucket: BucketDeDocumento, caminho: string): Promise<void> {
  const { error } = await supabase.storage.from(bucket).remove([caminho]);
  if (error) throw new Error(error.message);
}

// ─── Segurança (P-14) ───────────────────────────────────────────────

export interface SegurancaDoCondominio {
  possui_projeto_tecnico: boolean | null;
  projeto_tecnico_observacao: string | null;
  brigada_treinada: boolean | null;
  brigada_ultimo_treinamento: string | null;
  extintores_ultima_recarga: string | null;
  mangueiras_ultima_manutencao: string | null;
  observacoes: string | null;
}

export const SEGURANCA_VAZIA: SegurancaDoCondominio = {
  possui_projeto_tecnico: null,
  projeto_tecnico_observacao: null,
  brigada_treinada: null,
  brigada_ultimo_treinamento: null,
  extintores_ultima_recarga: null,
  mangueiras_ultima_manutencao: null,
  observacoes: null,
};

export async function lerSeguranca(condominioId: string): Promise<SegurancaDoCondominio | null> {
  const { data, error } = await supabase
    .from('condominio_seguranca')
    .select('*')
    .eq('condominio_id', condominioId)
    .maybeSingle();

  if (error) throw new Error(error.message);
  return data ? ({ ...SEGURANCA_VAZIA, ...data } as SegurancaDoCondominio) : null;
}

export async function gravarSeguranca(
  condominioId: string,
  dados: SegurancaDoCondominio,
): Promise<void> {
  const { error } = await supabase
    .from('condominio_seguranca')
    .upsert(
      { condominio_id: condominioId, ...dados, editado_em: new Date().toISOString() },
      { onConflict: 'condominio_id' },
    );
  if (error) throw new Error(error.message);
}

// ─── Licenças (P-15) ────────────────────────────────────────────────

export interface LicencasDoCondominio {
  regular_prefeitura: boolean | null;
  houve_reforma_acrescimo: boolean | null;
  havera_reforma_acrescimo: boolean | null;
  observacoes: string | null;
}

export const LICENCAS_VAZIAS: LicencasDoCondominio = {
  regular_prefeitura: null,
  houve_reforma_acrescimo: null,
  havera_reforma_acrescimo: null,
  observacoes: null,
};

export async function lerLicencas(condominioId: string): Promise<LicencasDoCondominio | null> {
  const { data, error } = await supabase
    .from('condominio_licencas')
    .select('*')
    .eq('condominio_id', condominioId)
    .maybeSingle();

  if (error) throw new Error(error.message);
  return data ? ({ ...LICENCAS_VAZIAS, ...data } as LicencasDoCondominio) : null;
}

export async function gravarLicencas(
  condominioId: string,
  dados: LicencasDoCondominio,
): Promise<void> {
  const { error } = await supabase
    .from('condominio_licencas')
    .upsert(
      { condominio_id: condominioId, ...dados, editado_em: new Date().toISOString() },
      { onConflict: 'condominio_id' },
    );
  if (error) throw new Error(error.message);
}

// ─── Manutenções (P-16) ─────────────────────────────────────────────

export interface Manutencao {
  id: string;
  condominioId: string;
  infraestruturaId: string;
  infraestruturaNome: string;
  infraestruturaLocalizacao: string | null;
  data: string;
  tipoId: string;
  descricao: string;
  responsavel: string | null;
  prestadorId: string | null;
  prestadorNome: string | null;
  /** Em centavos, como o banco guarda. */
  custoCentavos: number | null;
  observacoes: string | null;
  anexos: AnexoDeDocumento[];
}

const COLUNAS_MANUTENCAO =
  'id, condominio_id, infraestrutura_id, data, tipo_id, descricao, responsavel, ' +
  'prestador_id, custo_centavos, observacoes, anexos, ' +
  'infraestrutura ( nome, localizacao ), prestadores ( nome )';

function paraManutencao(row: any): Manutencao {
  return {
    id: row.id,
    condominioId: row.condominio_id,
    infraestruturaId: row.infraestrutura_id,
    infraestruturaNome: row.infraestrutura?.nome ?? '—',
    infraestruturaLocalizacao: row.infraestrutura?.localizacao ?? null,
    data: row.data,
    tipoId: row.tipo_id,
    descricao: row.descricao,
    responsavel: row.responsavel,
    prestadorId: row.prestador_id,
    prestadorNome: row.prestadores?.nome ?? null,
    custoCentavos: row.custo_centavos,
    observacoes: row.observacoes,
    anexos: Array.isArray(row.anexos) ? row.anexos : [],
  };
}

export async function lerManutencoes(
  condominioId: string,
  infraestruturaId?: string,
): Promise<Manutencao[]> {
  let consulta = supabase
    .from('manutencoes')
    .select(COLUNAS_MANUTENCAO)
    .eq('condominio_id', condominioId)
    .order('data', { ascending: false });

  if (infraestruturaId) consulta = consulta.eq('infraestrutura_id', infraestruturaId);

  const { data, error } = await consulta;
  if (error) throw new Error(error.message);
  return (data ?? []).map(paraManutencao);
}

export interface ManutencaoParaGravar {
  id?: string;
  condominioId: string;
  infraestruturaId: string;
  data: string;
  tipoId: string;
  descricao: string;
  responsavel: string | null;
  prestadorId: string | null;
  custoCentavos: number | null;
  observacoes: string | null;
  anexos: AnexoDeDocumento[];
}

/**
 * Grava a manutenção.
 *
 * `condominio_id` vai na linha porque a coluna é `not null`, mas quem decide é
 * o gatilho `manutencoes_condominio`: ele sobrescreve com o condomínio do item
 * de infraestrutura. Se a tela mandar outro, o banco corrige em vez de gravar
 * uma linha que a RLS de um Tenant esconde e a de outro mostra.
 *
 * Devolve o id porque o anexo precisa dele para montar o caminho no bucket, e
 * uma manutenção que ainda não foi gravada não tem id. Por isso o registro
 * nasce antes do arquivo — o contrário deixaria arquivo órfão no storage.
 */
export async function gravarManutencao(m: ManutencaoParaGravar): Promise<string> {
  const linha = {
    condominio_id: m.condominioId,
    infraestrutura_id: m.infraestruturaId,
    data: m.data,
    tipo_id: m.tipoId,
    descricao: m.descricao,
    responsavel: m.responsavel,
    prestador_id: m.prestadorId,
    custo_centavos: m.custoCentavos,
    observacoes: m.observacoes,
    anexos: m.anexos,
    editado_em: new Date().toISOString(),
  };

  if (m.id) {
    const { error } = await supabase.from('manutencoes').update(linha).eq('id', m.id);
    if (error) throw new Error(error.message);
    return m.id;
  }

  const { data, error } = await supabase.from('manutencoes').insert(linha).select('id').single();

  if (error) throw new Error(error.message);
  return data.id as string;
}

export async function excluirManutencao(id: string): Promise<void> {
  const { error } = await supabase.from('manutencoes').delete().eq('id', id);
  if (error) throw new Error(error.message);
}
